import { Link } from 'react-router-dom';

const BotanicalCocktail = () => {
  return (
    <section className="py-32 px-6 lg:px-12 bg-warmWhite">
      <div className="max-w-5xl mx-auto">

        {/* Section Label */}
        <div className="flex items-center gap-3 mb-12">
          <div className="w-10 h-[1px] bg-bronze/60"></div>
          <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.3em] uppercase">
            Vionne Stability Botanical Cocktail
          </span>
          <div className="w-10 h-[1px] bg-bronze/60"></div>
        </div>

        {/* Beyond the Treatment Room */}
        <div className="mb-20">
          <h2 className="font-display text-3xl md:text-4xl font-light text-charcoal italic mb-8">
            Beyond the Treatment Room
          </h2>
          <div className="space-y-5 text-textGray text-[13px] font-sans font-light leading-relaxed max-w-3xl">
            <p>
              The Vionne Stability Botanical formulations are not separate products. They are designed to work alongside the Stability Method. While clinical treatments initiate regulation, botanical formulations help support and stabilize these changes over time.
            </p>
            <p>
              Rather than relying on standardized formulas, Vionne Stability works through individualized botanical preparations. Each blend is created in response to the patterns observed during treatment—translating clinical insight into a formulation designed for daily life. Allowing each blend to support your body's specific regulatory needs identified during your session.
            </p>
            <p className="text-charcoal/80 italic">
              This creates a bridge between clinical treatment and everyday restoration.
            </p>
          </div>
        </div>

        {/* Divider Arrow */}
        <div className="flex justify-center mb-20">
          <div className="flex flex-col items-center gap-1 opacity-30">
            <div className="w-[1px] h-8 bg-charcoal"></div>
            <i className="ri-arrow-down-s-line text-charcoal text-lg"></i>
          </div>
        </div>

        {/* The Formulation Process */}
        <div id="cocktail-formulation" className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-6 h-[1px] bg-bronze/60"></div>
            <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.3em] uppercase">
              The Formulation Process
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-charcoal text-[14px] font-sans font-light leading-relaxed mb-6">
                A bespoke botanical preparation created during your session.
              </p>
              <p className="text-textGray text-[13px] font-sans font-light leading-relaxed mb-6">
                Each formulation is crafted one-on-one based on:
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Current physical state',
                  'Emotional and nervous system patterns',
                  'Scent preference',
                  'Most suitable application format',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-textGray text-[13px] font-sans font-light">
                    <span className="mt-[5px] w-1 h-1 rounded-full bg-bronze/60 flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-textGray text-[13px] font-sans font-light leading-relaxed italic">
                Available as oil, serum, mist, balm, or cream, etc.
              </p>
              <div className="mt-6 pt-5 border-t border-warmGray/15">
                <p className="text-charcoal text-[13px] font-sans font-light leading-relaxed mb-2">
                  Formulations are typically recommended following a Stability treatment session.
                </p>
                <p className="text-textGray text-[13px] font-sans font-light leading-relaxed">
                  Every preparation is entirely individualized and produced in small batches.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden border border-warmGray/10 shadow-soft">
              <img
                src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/5a8ddb2dd17506b799f4099092813fa9.png"
                alt="Botanical Formulation"
                className="w-full object-cover object-center"
                style={{ height: '400px' }}
              />
            </div>
          </div>
        </div>

        {/* Divider Arrow */}
        <div className="flex justify-center mb-20">
          <div className="flex flex-col items-center gap-1 opacity-30">
            <div className="w-[1px] h-8 bg-charcoal"></div>
            <i className="ri-arrow-down-s-line text-charcoal text-lg"></i>
          </div>
        </div>

        {/* Application Formats */}
        <div id="application-formats">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-6 h-[1px] bg-bronze/60"></div>
            <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.3em] uppercase">
              Application Formats
            </span>
          </div>

          <p className="text-textGray text-[13px] font-sans font-light leading-relaxed mb-8 max-w-2xl">
            Clients often incorporate their formulations into daily rituals such as:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { icon: 'ri-moon-line', label: 'Evening nervous system unwinding' },
              { icon: 'ri-sun-line', label: 'Morning circulation activation' },
              { icon: 'ri-heart-pulse-line', label: 'Hormonal rhythm support' },
              { icon: 'ri-sparkling-line', label: 'Facial vitality maintenance' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 border border-warmGray/10 flex flex-col items-start gap-3 hover:shadow-soft transition-shadow duration-300"
              >
                <div className="w-8 h-8 flex items-center justify-center text-bronze">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <p className="text-charcoal text-[12px] font-sans font-light leading-relaxed">
                  {item.label}
                </p>
              </div>
            ))}
          </div>

          <p className="text-textGray text-[13px] font-sans font-light leading-relaxed italic max-w-2xl">
            These small practices help reinforce the regulatory work initiated during treatment.
          </p>

          <p className="text-charcoal text-[13px] font-sans font-semibold leading-relaxed mt-5 max-w-2xl">
            Stability is cultivated not only through treatment, but also through the small rituals that shape daily life.
          </p>

          {/* CTA Button */}
          <div className="mt-10">
            <Link
              to="/booking"
              className="inline-flex items-center gap-3 px-8 py-3.5 border border-bronze/60 text-bronze text-[11px] font-sans font-semibold tracking-[0.2em] uppercase rounded-full hover:bg-bronze hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-line text-sm"></i>
              Enhance Your Treatment
            </Link>
          </div>

        </div>

      </div>
    </section>
  );
};

export default BotanicalCocktail;
